import _ from 'underscore';
import Axios from 'axios';
import { Helpers } from '../../ts/helpers/Helpers';
import { IOAuthCredentialTypeInfo } from '../../ts/ServerApi/types/IOAuthCredential';
import { OAuthCredentialsService } from './OAuthCredentialsService';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { SessionService } from '~commonInfra/services/SessionService';
import { HelpService } from '~commonInfra/services/HelpService';
import { OpenWindowSpecs, WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { SimpleDeferred } from '@tableau/vizportalclient-common-components';


export interface IAddOAuthResponseListenerWindow extends Window {
  tableauPopupComplete(response?: string): void;
}

// NOTE: Keep this in sync with the code in embeddedCommon.pug
const OAuthPath = '/auth/request_oauth';
const WindowName = 'TableauAuth';
const AddGenericAuthCredentialsPath = '/auth/add_generic_auth_credentials';
const InitMsgName = 'init';
const AuthPhaseName = 'auth';

let openWindow: Window;
let deferred = new SimpleDeferred<string>();
// Based on different connectionType, we will use try auth editing in different ways
// especially for first-class WDC-based connections
let _dataConnectionType: string;
let _instanceUrl: string;
let _role: string;


function handleResponse(response: string = '') {
  if (OAuthCredentialsService.supportsGenericAuth(_dataConnectionType)) {
    handleGenericAuthResponse(response);
  } else {
    handleCustomOAuthResponse(response);
  }
}

function handleGenericAuthResponse(response: string) {
  const responseObj = JSON.parse(response);
  if (responseObj.msgName === 'loaded') {
    // When the home page for WDC is loaded, post a message to initialize it
    sendInitAuthMessage();
  } else if (responseObj.msgName === 'submit') {
    saveGenericAuthCredentials(responseObj);
  }
}

function saveGenericAuthCredentials(responseObj: any) {
  const formData = new FormData();
  formData.append('data_class', _dataConnectionType);
  formData.append('username', responseObj.props.username);
  formData.append('password', responseObj.props.password);
  if (_instanceUrl) {
    formData.append('instance_url', _instanceUrl);
  }
  if (_role) {
    formData.append('role', _role);
  }

  if (responseObj.props.usernameAlias) {
    formData.append('username_alias', responseObj.props.usernameAlias);
  }

  const addCredentialPromise = Axios({
    method: 'POST',
    transformRequest: (data: any) => data,
    headers: {
      'Content-Type': undefined,
      'X-XSRF-TOKEN': SessionService.getSessionId()
    },
    url: AddGenericAuthCredentialsPath,
    data: formData
  });

  addCredentialPromise.then((result: any) => {
    const data = JSON.stringify(result.data);
    handleCustomOAuthResponse(data);
  });
}

function sendInitAuthMessage() {
  if (!openWindow) {
    return;
  }

  const version = getSessionManagementServiceInstance().sessionServerVersion();

  // For first-class WDC, load WDC url and send an init message to the window
  const emptyMessage = {
    'msgRecipient': 'webdataconnector',
    'msgName': InitMsgName,
    'msgData': { 'phase': AuthPhaseName },
    'props': {
      'authPurpose': 'enduring',
      'connectionName': '',
      'connectionData': '',
      'password': '',
      'username': '',
      'incrementalExtractColumn': '',
      'versionNumber': '2.0', // This is for WDC shim libary 2.0.* versions
      'locale': HelpService.helpUrlLanguage(),
      'platformBuildNumber': version.build,
      'platformEdition': 'server',
      'platformOS': version.platform,
      'platformVersion': HelpService.externalVersion()
    }
  };

  openWindow.postMessage(JSON.stringify(emptyMessage), '*');
}

function handleCustomOAuthResponse(response: string) {
  const errorCodeMatch = response.match(/errorCode=([^(&|")]*)/);
  if(errorCodeMatch) {
    const errorCode = parseInt(errorCodeMatch[1], 10) || 0;
    notifyError(errorCode);
    return;
  }

  const newCredentialIdMatch = response.match(/id=([^(&|")]*)/);
  if(newCredentialIdMatch) {
    notifySuccess(newCredentialIdMatch[1]);
    return;
  }

  notifySuccess('');
}

function removeOpenWindow() {
  try {
    openWindow.close();
  } catch (e) {
  } // Closing the window is best effort so there's nothing to do if it fails

  openWindow = null;
}

function notifySuccess(credentialId?: string) {
  deferred.resolve(credentialId);
  removeOpenWindow();
}

function notifyError(errorCode?: number) {
  deferred.reject(errorCode);
  removeOpenWindow();
}

function getPopupSpecs(): OpenWindowSpecs {
  const popupOptions: OpenWindowSpecs = {
    width: 800,
    height: 600,
    toolbar: 1,
    resizable: 1
  };

  // IE8 polyfills. Note that outer* ones are not quite right for IE8, but good enough. B147312
  const screenLeft = window.screenLeft || window.screenX;
  const outerWidth = window.outerWidth || window.innerWidth;
  const screenTop = window.screenTop || window.screenY;
  const outerHeight = window.outerHeight || window.innerHeight;


  // Open horizontally centered on current window
  popupOptions.left = screenLeft + (outerWidth - popupOptions.width) / 2;

  // Open a small amount from the top
  popupOptions.top = screenTop + (outerHeight - popupOptions.height) / 8;

  return popupOptions;
}

export namespace AddOAuthCredentialAction {
    // SET UP GLOBAL LISTENERS

    (<any>window).tableauPopupComplete = (response: string) => handleResponse(response);

    $(window).on('message', (jqEvent: JQuery.TriggeredEvent) => {
      // casted to <any> because JQueryEvent originalEvent is expected to be a PopStateEvent which is not compatible with MessageEvent
      const originalEvent: MessageEvent = <any>jqEvent.originalEvent;

      if(originalEvent.source !== openWindow) return;

      const response: string = originalEvent.data;

      if(!_.isString(response)) return;

      handleResponse(response);
    });

    // The user is navigating away from this page so the popup is no longer needed, let's try to remove it for them.
    $(window).on('unload', () => removeOpenWindow());

  // Public methods

  export function href(typeInfo: IOAuthCredentialTypeInfo, instanceurl?: string,
      role?: string): string {
    // Redirect to server which will take care of redirecting to the appropriate place based on data_class
    interface OAuthCredentialInfo {
      'data_class': string;
      'instanceURL'?: string;
      'role'?: string;
    }
    let oauthCredentialInfo: OAuthCredentialInfo = {'data_class': typeInfo.type};
    if (instanceurl) {
      oauthCredentialInfo.instanceURL = instanceurl;
    }
    if (role) {
      oauthCredentialInfo.role = role;
    }
    const serialized = Helpers.serialize(oauthCredentialInfo);
    return OAuthPath + '?' + serialized + '&' +
      Helpers.serialize({ 'secure' : (window.location.protocol.toLowerCase() === 'https:') });
  }

  export function attemptAuth(connectionType: string, instanceurl: string = null, role: string = null): Promise<string> {
    cancelPendingAuthAttempt();

    const typeInfo = OAuthCredentialsService.getTypeInfo(connectionType);

    deferred = new SimpleDeferred<string>();
    _instanceUrl = instanceurl;
    _role = role;
    _dataConnectionType = typeInfo.type;
    openWindow = WindowLocationService.open(href(typeInfo, instanceurl, role),
     WindowName, getPopupSpecs());

    try{
      openWindow.focus(); // Bring the newly opened window to the front
    } catch(e) {}

    return deferred.promise;
  }

  export function cancelPendingAuthAttempt() {
    return notifyError();
  }
}

export type AddOAuthCredentialAction = typeof AddOAuthCredentialAction;
